import { HStack, Spacer, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuMessageSquare, LuPencil, LuPlus } from "react-icons/lu";
import { useParams } from "react-router-dom";

import {
  deleteMessagingPillar,
  getCustomerProfileSubsets,
  getMessagingPillars,
} from "../../../api/company-knowledge";
import { Button } from "../../../components/button";
import DataTable, { DataTableColumn } from "../../../components/data-table";
import { DeleteButton } from "../../../components/delete-button";
import { EmptyState } from "../../../components/empty-state";
import IconCircle from "../../../components/icon-circle";
import { MessagingPillarDialog } from "../../../components/messaging-pillar-dialog";
import PageBody from "../../../components/page-body";
import { useLoading, useToaster } from "../../../hooks";
import {
  CustomerProfileSubset,
  MessagingPillar,
} from "../../../types/company-knowledge";

export default function MessagingPillarsPage() {
  const { workspace: workspaceSlug } = useParams();
  const [pillars, setPillars] = React.useState<MessagingPillar[]>([]);
  const [subsets, setSubsets] = React.useState<CustomerProfileSubset[]>([]);
  const [isLoading, withLoading] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editingPillar, setEditingPillar] =
    React.useState<MessagingPillar | null>(null);
  const withToaster = useToaster();

  const loadPillars = React.useCallback(async () => {
    if (!workspaceSlug) return;
    await withLoading(async () => {
      await withToaster(async () => {
        // The subsets ride along on every load so the "Applicable
        // ICP Subsets" column and the dialog's checkboxes stay in
        // sync.
        const [pillars, subsets] = await Promise.all([
          getMessagingPillars(workspaceSlug),
          getCustomerProfileSubsets(workspaceSlug),
        ]);
        setPillars(pillars);
        setSubsets(subsets);
        setHasLoadedOnce(true);
      });
    });
  }, [withLoading, withToaster, workspaceSlug]);

  React.useEffect(() => {
    loadPillars();
  }, [loadPillars]);

  const handleDelete = React.useCallback(
    async (id: number) => {
      if (!workspaceSlug) return;
      await withLoading(async () => {
        await withToaster(async () => {
          await deleteMessagingPillar(workspaceSlug, id);
          await loadPillars();
        });
      });
    },
    [loadPillars, withLoading, withToaster, workspaceSlug],
  );

  const columns: DataTableColumn<MessagingPillar>[] = [
    {
      id: "name",
      name: "Name",
      icon: <LuMessageSquare />,
      width: 240,
      cell: ({ value }) => <Text fontWeight="medium">{value.name}</Text>,
    },
    {
      id: "type",
      name: "Type",
      width: 120,
      cell: ({ value }) => (
        <Text color="fg.muted">
          {pillarTypeLabels[value.type] ?? value.type}
        </Text>
      ),
    },
    {
      id: "description",
      name: "Description",
      width: 320,
      cell: ({ value }) => (
        <Text color="fg.muted" truncate>
          {value.description || "—"}
        </Text>
      ),
    },
    {
      name: "Applicable ICP subsets",
      width: 200,
      cell: ({ value }) => {
        const names = subsets
          .filter((s) => value["subset-ids"].includes(s.id))
          .map((s) => s.name);
        return (
          <Text color="fg.muted" truncate>
            {names.length > 0 ? names.join(", ") : "All subsets"}
          </Text>
        );
      },
    },
    {
      name: "",
      width: 100,
      cell: ({ value }) => (
        <HStack justify="flex-end">
          <Button
            size="sm"
            title="Edit messaging pillar"
            variant="ghost"
            onClick={() => {
              setEditingPillar(value);
              setIsDialogOpen(true);
            }}
          >
            <LuPencil />
          </Button>
          <DeleteButton
            label="Delete"
            message={`Delete "${value.name}"?`}
            onDelete={() => handleDelete(value.id)}
          />
        </HStack>
      ),
    },
  ];

  const isEmpty = pillars.length === 0 && hasLoadedOnce;

  return (
    <PageBody>
      <Stack flex="1" gap="5">
        <HStack>
          <Stack gap="0.5">
            <Text color="fg.ink" fontSize="md" fontWeight="medium">
              Messaging Pillars
            </Text>
            <Text color="fg.muted" fontSize="sm">
              The ideas your messaging is built on: buyer pains, benefits, and
              capabilities.
            </Text>
          </Stack>
          {!isEmpty && (
            <>
              <Spacer />
              <Button
                onClick={() => {
                  setEditingPillar(null);
                  setIsDialogOpen(true);
                }}
              >
                <LuPlus /> Create Messaging Pillar
              </Button>
            </>
          )}
        </HStack>
        {isEmpty ? (
          <HStack flex="1" alignItems="center">
            <EmptyState
              icon={
                <IconCircle Icon={LuMessageSquare} size="md" variant="flat" />
              }
              title="No Messaging Pillars"
              description="Add the pillars your product's messaging is built on."
            >
              <Button
                onClick={() => {
                  setEditingPillar(null);
                  setIsDialogOpen(true);
                }}
              >
                <LuPlus /> Create Messaging Pillar
              </Button>
            </EmptyState>
          </HStack>
        ) : (
          <DataTable
            data={pillars}
            columns={columns}
            skeleton={isLoading && hasLoadedOnce ? 3 : undefined}
          />
        )}
      </Stack>
      <MessagingPillarDialog
        isOpen={isDialogOpen}
        onOpenChange={({ open }) => {
          if (!open) setEditingPillar(null);
          setIsDialogOpen(open);
        }}
        onSuccess={loadPillars}
        pillar={editingPillar}
        subsets={subsets}
        workspaceSlug={workspaceSlug ?? ""}
      />
    </PageBody>
  );
}

// Display-only; the stored and submitted value stays lowercase
// ("pain" | "benefit" | "capability").
const pillarTypeLabels: Record<string, string> = {
  pain: "Pain",
  benefit: "Benefit",
  capability: "Capability",
};
